import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import useAuth from '../../../Hooks/useAuth';
import AdminLayout from '../../../components/AdminLayout/AdminLayout';
import DashboardMainContainer from '../../../components/common/Containers/DashboardMainContainer';
import ConfigurationListPaginationFooter from '../../../components/AdminDashboard/Configuration/ConfigurationListPaginationFooter';
import { ACTION_POPUP_CONTENT_WIDTH_CLASS } from '../../../components/popup/popupLayoutSpec';
import { useInlineRowActions, ROW_ACTIONS_MENU_HEIGHT } from '../../../Hooks/useInlineRowActions';
import { useConfigurationListPagination } from '../../../Hooks/useConfigurationListPagination';
import {
  AdminPageTitle,
  AdminPanelTitle,
  AdminTableHeader,
  AdminTableBody,
  AdminTableMeta,
  AdminStatusBadge,
  AdminRowMenuItem,
  AdminRowActionsButton,
  AdminFixedRowActionsMenu,
  AdminRowMenuIcon,
  AdminSupportingText,
  AdminHeaderAction,
  ADMIN_PAGE_TITLE_CLASS,
  ADMIN_TABLE_CLASS,
} from '../shared/adminUx';
import { buildSampleFormInventory } from '../../../data/formInventoryConfig';
import {
  FORM_INVENTORY_CONFIGURATION_ROUTE,
  LIST_PREVIEW_COUNT,
  MESSAGES,
  buildSampleFormInventoryConfigurationRules,
  enrichRuleFromInventory,
  formatEffectsDisplay,
  formatFormInventoryDate,
  formatTargetDisplay,
  getConditionPreviewLabels,
} from '../../../data/formInventoryConfigurationConfig';
import {
  canAccessFormInventoryConfiguration,
  canAddFormInventoryConfiguration,
  canEditFormInventoryConfiguration,
} from '../../../data/formInventoryConfigurationWorkflow';

const tableCols =
  'minmax(100px, 0.7fr) minmax(120px, 1.15fr) minmax(80px, 0.55fr) minmax(88px, 0.65fr) minmax(120px, 1fr) minmax(110px, 0.9fr) minmax(80px, 0.55fr) minmax(68px, 0.42fr) minmax(110px, 0.8fr) minmax(72px, 0.48fr) minmax(72px, 0.48fr) minmax(48px, 0.04fr)';

const TABLE_MIN_WIDTH = '1180px';

const ViewAllModal = ({ title, items, onClose }) => (
  <div
    className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 px-4"
    onClick={onClose}
    role="presentation"
  >
    <div
      className={`bg-white rounded-xl shadow-lg ${ACTION_POPUP_CONTENT_WIDTH_CLASS} max-h-[90vh] overflow-y-auto`}
      onClick={(e) => e.stopPropagation()}
      role="dialog"
      aria-modal="true"
      aria-labelledby="form-cfg-view-all-title"
    >
      <div className="relative px-5 pt-5 pb-3 border-b border-lightgray">
        <div id="form-cfg-view-all-title">
          <AdminPanelTitle className="text-offblack text-center">{title}</AdminPanelTitle>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="absolute right-4 top-4 text-offblack hover:text-primaryblue"
          aria-label="Close"
        >
          <i className="fi fi-rs-cross text-sm" aria-hidden />
        </button>
      </div>
      <div className="px-5 py-4">
        <ul className="divide-y divide-lightgray border-y border-lightgray">
          {items.map((item, index) => (
            <li key={`${item}-${index}`} className="py-2.5 break-words">
              <AdminTableBody>{item}</AdminTableBody>
            </li>
          ))}
        </ul>
      </div>
    </div>
  </div>
);

const PreviewListCell = ({ items, emptyLabel = '—', onViewAll }) => {
  if (!items || items.length === 0) {
    return (
      <AdminTableBody className="truncate block" title={emptyLabel}>
        {emptyLabel}
      </AdminTableBody>
    );
  }

  const preview = items.slice(0, LIST_PREVIEW_COUNT).join(', ');
  const fullText = items.join(', ');

  if (items.length <= LIST_PREVIEW_COUNT) {
    return (
      <AdminTableBody className="truncate block" title={fullText}>
        {preview}
      </AdminTableBody>
    );
  }

  return (
    <AdminTableBody className="block min-w-0 w-full" title={fullText}>
      <span className="inline-flex min-w-0 max-w-full items-baseline flex-wrap gap-x-1">
        <span className="min-w-0 truncate">{preview}</span>
        <button
          type="button"
          className="text-primaryblue hover:underline shrink-0 bg-white relative z-[1]"
          onClick={(e) => {
            e.stopPropagation();
            onViewAll?.();
          }}
        >
          View All
        </button>
      </span>
    </AdminTableBody>
  );
};

const FormInventoryConfiguration = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { auth } = useAuth();
  const canAccess = canAccessFormInventoryConfiguration(auth);
  const canAdd = canAddFormInventoryConfiguration(auth);
  const canEdit = canEditFormInventoryConfiguration(auth);

  const [forms] = useState(() => location.state?.forms ?? buildSampleFormInventory());
  const [rules, setRules] = useState(() => {
    const incoming = location.state?.rules;
    if (Array.isArray(incoming)) return incoming.map((rule) => enrichRuleFromInventory(rule, forms));
    return buildSampleFormInventoryConfigurationRules(forms);
  });

  const [viewAllModal, setViewAllModal] = useState(null);
  const [feedback, setFeedback] = useState(location.state?.saveSuccess || '');

  const {
    rowActionsOpen,
    rowActionsRefs,
    tableScrollRef,
    rowActionsMenuLayout,
    rowActionsMenuRef,
    rowActionsMenuPlacementReady,
    toggleRowActions,
    closeRowActions,
  } = useInlineRowActions(ROW_ACTIONS_MENU_HEIGHT.DOUBLE);

  useEffect(() => {
    const incoming = location.state?.rules;
    const success = location.state?.saveSuccess;
    if (!incoming && !success) return;
    if (Array.isArray(incoming)) {
      setRules(incoming.map((rule) => enrichRuleFromInventory(rule, forms)));
    }
    if (success) setFeedback(success);
    navigate(location.pathname, { replace: true, state: {} });
  }, [location.state, location.pathname, navigate, forms]);

  const enrichedRules = useMemo(
    () => rules.map((rule) => enrichRuleFromInventory(rule, forms)),
    [rules, forms],
  );

  const {
    currentPage,
    setCurrentPage,
    totalPages,
    paginatedItems: paginatedRules,
    rangeStart,
    rangeEnd,
    totalCount,
    showPagination,
  } = useConfigurationListPagination(enrichedRules);

  const sharedState = { rules: enrichedRules, forms };

  const openView = (rule) => {
    navigate(`${FORM_INVENTORY_CONFIGURATION_ROUTE}/${encodeURIComponent(rule.id)}`, {
      state: { rule, ...sharedState },
    });
  };

  const openEdit = (rule) => {
    navigate(`${FORM_INVENTORY_CONFIGURATION_ROUTE}/${encodeURIComponent(rule.id)}/edit`, {
      state: { rule, ...sharedState },
    });
  };

  const openAdd = () => {
    navigate(`${FORM_INVENTORY_CONFIGURATION_ROUTE}/add`, { state: sharedState });
  };

  if (!canAccess) {
    return (
      <AdminLayout>
        <AdminPageTitle>Form Inventory</AdminPageTitle>
        <AdminSupportingText className="text-mediumgray mt-4">
          You do not have permission to view Form Inventory configuration. Access is limited to the
          Platform Owner, Co-owner, and selected Platform Development Team Members with direct access.
        </AdminSupportingText>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="min-w-0">
        <div className={`flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 ${ADMIN_PAGE_TITLE_CLASS}`}>
          <AdminPageTitle className="!mb-0">Form Inventory</AdminPageTitle>
          {canAdd ? (
            <AdminHeaderAction type="button" onClick={openAdd}>
              Add Rule
            </AdminHeaderAction>
          ) : null}
        </div>

        {feedback ? (
          <AdminSupportingText className="text-freshgreen mb-4">{feedback}</AdminSupportingText>
        ) : null}

        <DashboardMainContainer className={`${ADMIN_TABLE_CLASS} max-w-full min-w-0`}>
          <div ref={tableScrollRef} className="overflow-x-auto">
            <div
              className="grid w-full py-3 text-offblack border-b border-lightgray px-4 gap-x-2 gap-y-2"
              style={{ gridTemplateColumns: tableCols, minWidth: TABLE_MIN_WIDTH }}
            >
              {[
                'Configuration ID',
                'Rule Name',
                'Owning Module',
                'Target Type',
                'Target',
                'Conditions',
                'Effects',
                'Rule Status',
                'Evaluation Status',
                'Date Added',
                'Last Updated',
                'Actions',
              ].map((label) => (
                <div
                  key={label}
                  className={`flex items-center min-w-0${label === 'Actions' ? ' shrink-0 justify-center' : ''}`}
                >
                  <AdminTableHeader className="truncate">{label}</AdminTableHeader>
                </div>
              ))}
            </div>

            {paginatedRules.length === 0 ? (
              <div className="px-4 py-10 text-center">
                <AdminSupportingText className="text-mediumgray mb-0">{MESSAGES.emptyState}</AdminSupportingText>
              </div>
            ) : (
              paginatedRules.map((rule) => {
                const conditionLabels = getConditionPreviewLabels(rule.conditions);
                const effectLabels = formatEffectsDisplay(rule.effects);
                const targetLabel = formatTargetDisplay(rule);
                return (
                  <div
                    key={rule.id}
                    role="button"
                    tabIndex={0}
                    onClick={() => openView(rule)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        openView(rule);
                      }
                    }}
                    className="grid w-full py-3 px-4 text-offblack border-b border-lightgray last:border-b-0 gap-x-2 gap-y-2 cursor-pointer rounded-sm outline-none focus-visible:ring-2 focus-visible:ring-primaryblue focus-visible:ring-offset-1"
                    style={{ gridTemplateColumns: tableCols, minWidth: TABLE_MIN_WIDTH }}
                  >
                    <div className="flex items-center min-w-0 overflow-hidden">
                      <AdminTableMeta className="truncate block" title={rule.id}>
                        {rule.id}
                      </AdminTableMeta>
                    </div>
                    <div className="flex items-center min-w-0 overflow-hidden">
                      <AdminTableBody className="truncate block" title={rule.ruleName}>
                        {rule.ruleName}
                      </AdminTableBody>
                    </div>
                    <div className="flex items-center min-w-0 overflow-hidden">
                      <AdminTableBody className="truncate block">{rule.owningModule || '—'}</AdminTableBody>
                    </div>
                    <div className="flex items-center min-w-0 overflow-hidden">
                      <AdminTableBody className="truncate block">{rule.targetType || '—'}</AdminTableBody>
                    </div>
                    <div className="flex items-center min-w-0 overflow-hidden">
                      <AdminTableBody className="truncate block" title={targetLabel}>
                        {targetLabel}
                      </AdminTableBody>
                    </div>
                    <div className="flex items-center min-w-0 overflow-hidden">
                      <PreviewListCell
                        items={conditionLabels}
                        onViewAll={() =>
                          setViewAllModal({ title: 'Conditions', items: conditionLabels })
                        }
                      />
                    </div>
                    <div className="flex items-center min-w-0 overflow-hidden">
                      <PreviewListCell
                        items={effectLabels}
                        onViewAll={() => setViewAllModal({ title: 'Effects', items: effectLabels })}
                      />
                    </div>
                    <div className="flex items-center min-w-0 overflow-hidden">
                      <AdminStatusBadge status={rule.ruleStatus} />
                    </div>
                    <div className="flex items-center min-w-0 overflow-hidden">
                      <AdminTableBody className="truncate block" title={rule.evaluationStatus}>
                        {rule.evaluationStatus || '—'}
                      </AdminTableBody>
                    </div>
                    <div className="flex items-center min-w-0 overflow-hidden">
                      <AdminTableMeta className="truncate block">
                        {formatFormInventoryDate(rule.dateAdded)}
                      </AdminTableMeta>
                    </div>
                    <div className="flex items-center min-w-0 overflow-hidden">
                      <AdminTableMeta className="truncate block">
                        {formatFormInventoryDate(rule.lastUpdated)}
                      </AdminTableMeta>
                    </div>
                    <div
                      className="flex items-center justify-center shrink-0 overflow-visible"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <div
                        className="relative inline-flex"
                        ref={(el) => {
                          rowActionsRefs.current[rule.id] = el;
                        }}
                      >
                        <AdminRowActionsButton
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleRowActions(rule.id);
                          }}
                          aria-expanded={!!rowActionsOpen[rule.id]}
                          title="Actions"
                          aria-label={`Actions for ${rule.ruleName}`}
                        >
                          <AdminRowMenuIcon />
                        </AdminRowActionsButton>
                        {rowActionsOpen[rule.id] && rowActionsMenuLayout ? (
                          <AdminFixedRowActionsMenu
                            layout={rowActionsMenuLayout}
                            menuRef={rowActionsMenuRef}
                            placementReady={rowActionsMenuPlacementReady}
                          >
                            <AdminRowMenuItem
                              onClick={(e) => {
                                e.stopPropagation();
                                closeRowActions();
                                openView(rule);
                              }}
                            >
                              View
                            </AdminRowMenuItem>
                            {canEdit ? (
                              <AdminRowMenuItem
                                onClick={(e) => {
                                  e.stopPropagation();
                                  closeRowActions();
                                  openEdit(rule);
                                }}
                              >
                                Edit
                              </AdminRowMenuItem>
                            ) : null}
                          </AdminFixedRowActionsMenu>
                        ) : null}
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>

          {showPagination ? (
            <ConfigurationListPaginationFooter
              rangeStart={rangeStart}
              rangeEnd={rangeEnd}
              totalCount={totalCount}
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={setCurrentPage}
            />
          ) : null}
        </DashboardMainContainer>
      </div>

      {viewAllModal ? (
        <ViewAllModal
          title={viewAllModal.title}
          items={viewAllModal.items}
          onClose={() => setViewAllModal(null)}
        />
      ) : null}
    </AdminLayout>
  );
};

export default FormInventoryConfiguration;
